import Quill from 'quill/core';
import React, { useRef, useState } from 'react';
import BorderedIcon from '../../../../components/BorderedIcon';
import DropdownMenu from '../../../../components/DropdownMenu';
import SelectEntityOption from '../../../../components/SelectEntityOption';
import { useQuill } from '../../../QuillContext';
import { textIcon } from '../../editorIcons';
import {
  BlockquoteItem,
  BulletedListItem,
  ChecklistItem,
  Heading1Item,
  Heading2Item,
  Heading3Item,
  HintDangerItem,
  HintInfoItem,
  HintSuccessItem,
  HintWarningItem,
  NumberedListItem,
} from '../../getSlashCommandMenuSections';
import ToolbarButton from './ToolbarButton';

const Parchment = Quill.import('parchment');

const PlainTextItem = {
  id: 'plain',
  data: {
    label: 'Normal text',
    icon: <BorderedIcon>{textIcon}</BorderedIcon>,
  },
};

const labelToFormat = {
  [Heading1Item.data.label]: { name: 'header', value: 1 },
  [Heading2Item.data.label]: { name: 'header', value: 2 },
  [Heading3Item.data.label]: { name: 'header', value: 3 },
  [BulletedListItem.data.label]: { name: 'list', value: 'bullet' },
  [NumberedListItem.data.label]: { name: 'list', value: 'ordered' },
  [ChecklistItem.data.label]: { name: 'list', value: 'unchecked' },
  [BlockquoteItem.data.label]: { name: 'blockquote', value: true },
  [HintInfoItem.data.label]: { name: 'hint', value: 'info' },
  [HintDangerItem.data.label]: { name: 'hint', value: 'error' },
  [HintSuccessItem.data.label]: { name: 'hint', value: 'success' },
  [HintWarningItem.data.label]: { name: 'hint', value: 'warning' },
};

export interface ToolbarObjectButtonOptions {
  defaultLabel: string;
  queryFormat(formatName: string): unknown;
  applyFormat(
    format: typeof labelToFormat[keyof typeof labelToFormat] | null,
  ): void;
}

interface ToolbarObjectButtonProps {
  disabled: boolean;
  options?: ToolbarObjectButtonOptions;
}

const ToolbarObjectButton = ({
  disabled,
  options,
}: ToolbarObjectButtonProps) => {
  const {
    quill,
    selection: { formats },
  } = useQuill();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [index, setIndex] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const queryFormat: ToolbarObjectButtonOptions['queryFormat'] =
    options?.queryFormat || ((formatName) => quill?.scroll.query(formatName));

  const applyFormat: ToolbarObjectButtonOptions['applyFormat'] =
    options?.applyFormat ||
    ((format) => {
      if (format) {
        quill?.format(format.name, format.value, Quill.sources.USER);
      } else {
        Object.keys(formats)
          .filter((name) => quill?.scroll.query(name, Parchment.Scope.BLOCK))
          .forEach((name) => quill?.format(name, null, Quill.sources.USER));
      }
    });

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const checkValue =
    formats.list === 'checked' ||
    formats.list === 'unchecked' ||
    (Array.isArray(formats.list) &&
      formats.list.sort().join('') === 'checkedunchecked');

  const activeLabel = checkValue
    ? ChecklistItem.data.label
    : Object.keys(labelToFormat).find((key) => {
        const format = labelToFormat[key];
        if (!format) return false;
        const { name, value } = format;
        return formats[name] && (!value || formats[name] === value);
      }) ||
      options?.defaultLabel ||
      PlainTextItem.data.label;

  const items = [
    PlainTextItem,
    Heading1Item,
    Heading2Item,
    BulletedListItem,
    NumberedListItem,
    ChecklistItem,
    BlockquoteItem,
  ].filter((item) => {
    const { label } = item.data;
    if (label === activeLabel) return false;
    const format = labelToFormat[label];
    return !format || !quill || queryFormat(format.name);
  });

  return (
    <ToolbarButton
      ref={buttonRef}
      disabled={disabled}
      // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
      popOver={(onClose) => (
        <DropdownMenu
          index={index}
          onIndexChange={setIndex}
          onClose={onClose}
          onSelect={({ data: { label } }) => {
            const format = labelToFormat[label];
            applyFormat(format || null);
            onClose();
          }}
          inputContainer={buttonRef.current}
          sections={[{ items }]}
          renderItem={(data) => <SelectEntityOption {...data} />}
        />
      )}
      popOverWidth="full"
      popOverOpen={dropdownOpen}
      onPopOverOpenChange={setDropdownOpen}
      testId="editToolbarObjectButton"
    >
      {activeLabel}
    </ToolbarButton>
  );
};

export default ToolbarObjectButton;
